import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NbDialogRef } from '@nebular/theme';
import { BucketService, FilePreviewInfo, IFile } from '../../../services/bucket.service';

@Component({
  selector: 'app-file-preview-panel',
  templateUrl: './file-preview-panel.component.html',
  styleUrl: './file-preview-panel.component.scss',
})
export class FilePreviewPanelComponent implements OnInit {
  @Input() file!: IFile;

  loading = true;
  error = false;
  previewInfo: FilePreviewInfo | null = null;
  safeUrl: SafeResourceUrl | null = null;
  officeViewerUrl: SafeResourceUrl | null = null;
  textContent: string | null = null;

  get fileType(): 'pdf' | 'text' | 'image' | 'office' | 'unsupported' {
    const fmt = this.previewInfo?.file_type || '';
    const name = (this.previewInfo?.file_name || '').toLowerCase();
    if (fmt === 'application/pdf') return 'pdf';
    if (fmt.startsWith('image/')) return 'image';
    if (fmt.startsWith('text/') || fmt === 'application/json' || name.endsWith('.md')) return 'text';
    if (/\.(docx?|xlsx?|pptx?)$/.test(name) || [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ].includes(fmt)) return 'office';
    return 'unsupported';
  }

  constructor(
    protected dialogRef: NbDialogRef<FilePreviewPanelComponent>,
    private bucketService: BucketService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    const fileId = this.file?.datatype_id || String(this.file?.id);
    if (!fileId) {
      this.error = true;
      this.loading = false;
      return;
    }

    this.bucketService.getPreviewUrl(fileId).subscribe({
      next: (info) => {
        this.previewInfo = info;
        // For PDFs: use native browser viewer with clean params (no toolbar, fit to width)
        const url = info.file_type === 'application/pdf'
          ? `${info.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`
          : info.url;
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        // For Office docs: use Microsoft Office Online viewer
        if (this.fileType === 'office') {
          const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(info.url)}`;
          this.officeViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(officeUrl);
        }
        if (this.fileType === 'text') {
          this.fetchTextContent(info.url);
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  private fetchTextContent(url: string) {
    fetch(url)
      .then(r => r.text())
      .then(text => {
        this.textContent = text;
        this.loading = false;
      })
      .catch(() => {
        this.error = true;
        this.loading = false;
      });
  }

  get fileSizeDisplay() {
    const size = this.previewInfo?.file_size || this.file?.file_size;
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  close() {
    this.dialogRef.close();
  }
}
